import {
  DEFAULT_BALANCE,
  validateShips,
  validateWeapons,
  type Balance,
  type DefIndex,
  type EnemyDef,
  type LevelDef,
  type ObstacleDef,
  type ShipDef,
  type WaveDef,
  type WeaponDef,
} from '../sim/defs';
import type { ModDef } from '../sim/mods';
import { ENDLESS_ROSTER } from './endless';
import { checkLevelObstacles } from './schema';
import { verifyTree, type MetaNodeDef, type MilestoneDef } from '../game/meta';
import { validateSecondaries, type SecondaryDef } from '../sim/secondary';
import { BASE_STATS, dps } from '../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Le registre.

   `import.meta.glob(..., { eager: true })` ramasse tous les fichiers `.def.ts`
   à la compilation. Conséquence directe et c'est tout l'objectif du jalon :
   **ajouter un ennemi = déposer un fichier**. Aucune liste à tenir à jour,
   aucun import à ajouter, aucune ligne de moteur à toucher — et donc aucun
   endroit où l'on puisse oublier de brancher son nouveau contenu.

   Le registre attribue aussi les identifiants NUMÉRIQUES. Les pools stockent
   des entiers — c'est ce qui rend les tableaux typés possibles — tandis que les
   fichiers de contenu se réfèrent à des chaînes lisibles. La conversion se fait
   ici, une fois, au chargement.
   ═══════════════════════════════════════════════════════════════════════════ */

interface Module<T> {
  readonly default: T;
}

function collect<T>(mods: Record<string, unknown>): T[] {
  const out: T[] = [];
  // Tri par chemin : l'ordre d'attribution des identifiants numériques devient
  // stable et reproductible. Sans ça, deux machines pourraient numéroter
  // différemment et les replays ne seraient plus portables.
  for (const key of Object.keys(mods).sort()) {
    const m = mods[key] as Module<T | T[]>;
    if (!m?.default) continue;
    // Un fichier peut exporter une définition OU un tableau. Les ennemis et les
    // niveaux méritent un fichier chacun — ils sont volumineux et se lisent
    // isolément. Vingt-trois mods de six lignes dans vingt-trois fichiers
    // seraient du bruit ; on les groupe par famille.
    if (Array.isArray(m.default)) out.push(...m.default);
    else out.push(m.default);
  }
  return out;
}

export function buildIndex(balance: Balance = DEFAULT_BALANCE): DefIndex {
  const enemyMods = import.meta.glob('./enemies/**/*.def.ts', { eager: true });
  // Les boss sont des ennemis comme les autres — seule leur taille les
  // distingue. Ils vivent dans un dossier séparé pour la lisibilité, pas parce
  // que le moteur en ferait quoi que ce soit de particulier.
  const bossMods = import.meta.glob('./bosses/**/*.def.ts', { eager: true });
  const waveMods = import.meta.glob('./waves/**/*.def.ts', { eager: true });
  const levelMods = import.meta.glob('./levels/**/*.def.ts', { eager: true });
  // Obstacles : décor collisionnable. Mêmes règles de ramassage que les ennemis
  // — déposer un `.def.ts` suffit. La vitrine (`vitrine.ts`) n'a pas ce suffixe :
  // elle n'est donc pas collectée comme une définition, ce qui est voulu.
  const obstacleMods = import.meta.glob('./obstacles/**/*.def.ts', { eager: true });
  const modMods = import.meta.glob('./mods/**/*.def.ts', { eager: true });
  const secondaryMods = import.meta.glob('./secondaries/**/*.def.ts', { eager: true });
  /* ⚠️ Mêmes règles que les secondaires, et le même piège : `collect` trie par
     CHEMIN, et le rang obtenu devient l'identifiant numérique. Renommer
     `aegis.def.ts` en `egide.def.ts` renumérote donc tout le catalogue derrière
     lui. Rien ne le signale, et les identifiants numériques entrent dans
     l'empreinte de replay via les statistiques qu'ils composent : une
     sauvegarde qui mémorise un rang plutôt qu'un identifiant désignerait un
     autre vaisseau après le renommage. D'où la règle, sans exception : on
     mémorise et on résout par IDENTIFIANT (voir `DEFAULT_SHIP_ID`), l'index
     numérique ne vit qu'entre deux appels. */
  const shipMods = import.meta.glob('./ships/**/*.def.ts', { eager: true });
  const weaponMods = import.meta.glob('./weapons/**/*.def.ts', { eager: true });
  const metaNodeMods = import.meta.glob('./meta/tree.def.ts', { eager: true });
  const metaMilestoneMods = import.meta.glob('./meta/milestones.def.ts', { eager: true });

  // La fusion préserve le tri par chemin — donc la stabilité des index
  // numériques, dont dépend la portabilité des replays.
  const enemies = collect<EnemyDef>({ ...enemyMods, ...bossMods });
  const secondaries = collect<SecondaryDef>(secondaryMods);
  const ships = collect<ShipDef>(shipMods);
  const weapons = collect<WeaponDef>(weaponMods);
  const waves = collect<WaveDef>(waveMods);
  const levels = collect<LevelDef>(levelMods);
  const mods = collect<ModDef>(modMods);
  const obstacles = collect<ObstacleDef>(obstacleMods);

  const enemyByI = new Map<string, number>();
  enemies.forEach((e, i) => enemyByI.set(e.id, i));
  const obstacleByI = new Map<string, number>();
  obstacles.forEach((o, i) => obstacleByI.set(o.id, i));

  const index: DefIndex = {
    enemies,
    enemyByI,
    waves: new Map(waves.map((w) => [w.id, w])),
    levels: new Map(levels.map((l) => [l.id, l])),
    obstacles,
    obstacleByI,
    mods,
    ships,
    shipByI: new Map(ships.map((s, i) => [s.id, i])),
    weapons,
    weaponByI: new Map(weapons.map((w, i) => [w.id, i])),
    secondaries,
    secondaryByI: new Map(secondaries.map((s, i) => [s.id, i])),
    metaNodes: collect<MetaNodeDef>(metaNodeMods),
    metaMilestones: collect<MilestoneDef>(metaMilestoneMods),
    // Roster du mode sans fin : une liste écrite, pas un glob (l'ordre des
    // paliers y est significatif à la lecture).
    endlessPool: ENDLESS_ROSTER,
    balance,
  };

  validate(index);
  return index;
}

/** Échoue BRUYAMMENT au chargement plutôt que discrètement en jeu.
 *
 *  Une référence cassée — une vague qui cite un ennemi renommé — produirait
 *  sinon une vague silencieusement vide, c'est-à-dire un trou de trente
 *  secondes dans un niveau que personne ne remarquerait avant la sortie. */
export function validate(index: DefIndex): void {
  const problems: string[] = [];

  const ids = new Set<string>();
  for (const e of index.enemies) {
    if (ids.has(e.id)) problems.push(`ennemi dupliqué : ${e.id}`);
    ids.add(e.id);
    if (e.hp <= 0) problems.push(`${e.id} : hp doit être > 0`);
    if (e.visualRadius <= 0) problems.push(`${e.id} : visualRadius doit être > 0`);
  }

  for (const w of index.waves.values()) {
    for (const s of w.spawns) {
      if (!index.enemyByI.has(s.enemy)) {
        problems.push(`vague ${w.id} : ennemi inconnu « ${s.enemy} »`);
      }
      if (s.at < 0 || s.at > w.duration) {
        problems.push(`vague ${w.id} : spawn à ${s.at}s hors de la durée ${w.duration}s`);
      }
      if (s.lane < 0 || s.lane > 6) {
        problems.push(`vague ${w.id} : couloir ${s.lane} hors de [0,6]`);
      }
      // Pièges de bord (PLAN.md §6.11) : un ennemi résistant dans un couloir
      // extrême acule le joueur contre le mur sans échappée.
      const def = index.enemies[index.enemyByI.get(s.enemy) ?? -1];
      if (def && def.hp > 200 && (s.lane === 0 || s.lane === 6)) {
        problems.push(`vague ${w.id} : ${s.enemy} (${def.hp} PV) interdit dans un couloir de bord`);
      }
    }
  }

  // Obstacles : mêmes garde-fous que les ennemis. Un rayon nul dessinerait un
  // rocher sans surface, invisible ET incollisionnable ; des PV nuls sur un
  // destructible le feraient éclater au premier tick de contact avec un tir.
  const obstacleIds = new Set<string>();
  for (const o of index.obstacles) {
    if (obstacleIds.has(o.id)) problems.push(`obstacle dupliqué : ${o.id}`);
    obstacleIds.add(o.id);
    if (o.radius <= 0) problems.push(`${o.id} : radius doit être > 0`);
    // Les PV ne sont lus que sur un destructible ; on ne les exige que là.
    if (o.destructible !== false && o.hp <= 0) {
      problems.push(`${o.id} : hp doit être > 0 sur un obstacle destructible`);
    }
  }

  for (const l of index.levels.values()) {
    for (const b of l.blocks) {
      if (!index.waves.has(b.wave)) {
        problems.push(`niveau ${l.id} : vague inconnue « ${b.wave} »`);
      }
      if (b.at > l.duration) {
        problems.push(`niveau ${l.id} : bloc à ${b.at}s au-delà de la durée ${l.duration}s`);
      }
    }
  }

  // Références croisées des obstacles posés dans les niveaux (types d'obstacles
  // et ennemis-sentinelles) : un identifiant renommé y ferait un placement
  // silencieusement vide, jamais une erreur.
  problems.push(...checkLevelObstacles(index));

  // Roster du mode sans fin : mêmes garde-fous que les vagues. Un identifiant
  // renommé y ferait un tirage silencieusement raté — un ennemi qui n'apparaît
  // jamais, sans erreur. Un poids nul le rendrait ininvocable ; un palier négatif
  // n'aurait aucun sens.
  for (const e of index.endlessPool) {
    if (!index.enemyByI.has(e.enemy)) {
      problems.push(`roster sans fin : ennemi inconnu « ${e.enemy} »`);
    }
    if (e.weight <= 0) problems.push(`roster sans fin : ${e.enemy} a un poids <= 0`);
    if (e.tier < 0) problems.push(`roster sans fin : ${e.enemy} a un palier négatif`);
  }

  // Les armes secondaires ont leurs propres règles d'équilibrage : une
  // secondaire qui dépasserait le DPS primaire cesserait d'être tactique pour
  // devenir l'arme principale, et le clic droit remplacerait le jeu.
  problems.push(...validateSecondaries(index.secondaries, dps(BASE_STATS)));

  /* Vaisseaux et armes primaires. Deux règles de conception, et elles disent la
     même chose sous deux formes : la méta ouvre des CHOIX, jamais de la
     puissance (PLAN.md §6.9).
       · un vaisseau doit renoncer à quelque chose ET compenser ;
       · trois armes doivent avoir le même DPS nominal, à ±10 % près.
     Sans elles, la dérive est mécanique : chaque nouveau contenu est réglé pour
     « se sentir » au moment où on l'écrit, c'est-à-dire un peu au-dessus du
     précédent, et l'arbre finit par vendre la victoire. */
  problems.push(...validateShips(index.ships));
  problems.push(...validateWeapons(index.weapons));

  /* ⚠️ L'assertion exigée par PLAN.md §6.9 : la somme des nœuds achetables ne
     peut pas dépasser le plafond composite de +35 %.

     Ce n'est pas une précaution de style. La méta est une couche de RÉTENTION,
     jamais une condition de victoire — un joueur excellent doit pouvoir finir
     le jeu à méta zéro. Sans ce garde-fou, ajouter un nœud « juste un peu »
     fort transforme progressivement la progression en mur de PV pour ceux qui
     ne l'ont pas, et le jeu devient injouable sans grind. */
  problems.push(...verifyTree(index.metaNodes, index.metaMilestones));

  if (problems.length > 0) {
    throw new Error(`Contenu invalide :\n  - ${problems.join('\n  - ')}`);
  }
}
